
import { Options, prop, Vue } from "vue-class-component";
import debounce from "lodash/debounce";
import {
  ErrorBookingV4,
  ReferenceChipV4,
  ReferenceTypeV4
} from "@/domain/entities/BookingV4";
import {
  classifyReference,
  noSttSchema,
  sttReferenceOptionalSchema,
  sttReferenceSchema,
  AccountTypeV4,
  FormContextV4
} from "./validationReferenceNumber";
import { parseZodError } from "../../../create-booking-v3/utils";
import { BookingController } from "@/app/ui/controllers/BookingController";
import { ClientNamePOSData } from "@/domain/entities/Partner";
import { PropType } from "vue";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { ConfigValidationForm } from "@/domain/entities/CreateBookingV3";
import { SttAdjustmentRule, SttReferenceExtValidation, SttManualValidation } from "@/domain/entities/ShipmentBooking";

const CHIP_TYPE_ORDER: string[] = [
  ReferenceTypeV4.STT_MANUAL,
  ReferenceTypeV4.SHIPMENT_ID,
  ReferenceTypeV4.EXTERNAL_REF
];

const CHIP_TYPE_LABEL_KEY: Record<string, string> = {
  [ReferenceTypeV4.STT_MANUAL]: "bookingV4.referenceNumber.type.sttManual",
  [ReferenceTypeV4.SHIPMENT_ID]: "bookingV4.referenceNumber.type.shipmentId",
  [ReferenceTypeV4.EXTERNAL_REF]: "bookingV4.referenceNumber.type.externalRef"
};

class Props {
  modelValue = prop<ReferenceChipV4[]>({
    default: () => [],
    type: Array
  });
  formContext = prop<FormContextV4>({
    required: true
  });
  accountType = prop<AccountTypeV4>({
    required: true
  });
  isRequired = prop<boolean>({
    default: false,
    type: Boolean
  });
  showValidationError = prop<boolean>({
    default: false,
    type: Boolean
  });
  errors = prop<ErrorBookingV4>({
    required: true
  });
  maxItems = prop<number>({
    default: 10,
    type: Number
  });
  selectedClient = prop<ClientNamePOSData | null>({
    default: null,
    type: Object as PropType<ClientNamePOSData | null>
  });
  // Adjust v4 rule-based disable (GENESIS-20184 continuation) — default EDITABLE
  configManualSttNo = prop<SttAdjustmentRule>({ default: new SttAdjustmentRule(), type: Object });
  configSttNoRefExternal = prop<SttAdjustmentRule>({ default: new SttAdjustmentRule(), type: Object });
  configShipmentId = prop<SttAdjustmentRule>({ default: new SttAdjustmentRule(), type: Object });
}

@Options({
  name: "ReferenceNumberV4",
  emits: ["update:modelValue", "update:errors", "validationChange"],
  watch: {
    modelValue: {
      deep: true,
      handler() {
        this.emitValidationChange();
      }
    },
    showValidationError(value: boolean) {
      if (value) this.syncAggregateError();
    },
    isRequired(value: boolean) {
      this.emitValidationChange();
      if (!value || this.showValidationError) this.syncAggregateError();
    },
    selectedClient: {
      deep: true,
      handler(newClient) {
        this.modelValue.forEach((chip: any) => {
          if (chip.type === ReferenceTypeV4.SHIPMENT_ID) {
            chip.status = "idle";
            chip.errorMessage = "";
            this.bumpChipSeq(chip);

            if (newClient?.clientCode) {
              this.validateChip(chip);
            }
          }
        });
        this.emitModelValue([...this.modelValue]);
        if (this.showValidationError) this.syncAggregateError();
      }
    }
  }
})
export default class ReferenceNumberV4 extends Vue.with(Props) {
  inputValue = "";
  public limitError = "";
  public duplicateTypeError = "";

  private refNumberDebouncers = new Map<ReferenceChipV4, () => void>();
  private sttManualDebouncers = new Map<ReferenceChipV4, () => void>();

  mounted() {
    this.emitValidationChange();
  }

  get orderedChips(): ReferenceChipV4[] {
    return [...this.modelValue].sort(
      (a, b) =>
        CHIP_TYPE_ORDER.indexOf(a.type) - CHIP_TYPE_ORDER.indexOf(b.type)
    );
  }

  chipTypeLabel(type: string): string {
    return this.$t(CHIP_TYPE_LABEL_KEY[type] || "") as string;
  }

  // A chip only counts once its async/sync validation has actually
  // resolved to "valid" — "idle"/"loading" must NOT be treated as valid,
  // otherwise validationChange fires true before validation completes.
  get isReferenceValid(): boolean {
    return this.modelValue.every((chip) => chip.status === "valid");
  }
  get isRefCanBeUsed(): boolean {
    const isOnlyExternalRef =
      this.modelValue.length > 0 &&
      this.modelValue.every(
        (chip) => chip.type === ReferenceTypeV4.EXTERNAL_REF
      );

    return (
      isOnlyExternalRef &&
      this.modelValue.every((chip) => chip.status === "valid") &&
      !this.referenceNumberError &&
      !this.limitErrorMessage &&
      !this.duplicateTypeError
    );
  }
  get isAtLimit(): boolean {
    return this.modelValue.length > this.maxItems;
  }

  get limitErrorMessage(): string {
    if (this.isAtLimit) {
      return this.$t("bookingV4.referenceNumber.maxLimit", {
        max: this.maxItems
      }) as string;
    }
    return this.limitError;
  }

  get referenceNumberError() {
    return this.errors?.referenceNumber || "";
  }

  get displayError(): boolean {
    return (
      this.modelValue.length > 0 &&
      !this.modelValue.every((chip) => chip.status === "valid")
    );
  }

  get hasVisibleError(): boolean {
    return Boolean(
      this.referenceNumberError ||
        (this.limitErrorMessage && this.modelValue.length > 0) ||
        (this.duplicateTypeError && this.modelValue.length > 0)
    );
  }

  // get hasValidExternalReference(): boolean {
  //   return this.modelValue.some(
  //     (chip) =>
  //       chip.type === ReferenceTypeV4.EXTERNAL_REF && chip.status === "valid"
  //   );
  // }

  hasValidExternalReference = false;

  get disabledManualStt(): boolean {
    return this.configManualSttNo.rule.includes(ConfigValidationForm.NON_EDITABLE);
  }

  get disabledRefExternal(): boolean {
    return this.configSttNoRefExternal.rule.includes(ConfigValidationForm.NON_EDITABLE);
  }

  get disabledShipmentId(): boolean {
    return this.configShipmentId.rule.includes(
      ConfigValidationForm.NON_EDITABLE
    );
  }

  emitModelValue(chips: ReferenceChipV4[]) {
    this.$emit("update:modelValue", chips);
  }

  emitValidationChange() {
    this.$emit("validationChange", this.isReferenceValid);
  }

  // Derives the aggregate error from current chip state (rather than a
  // single string mutated by whichever async validation call resolves
  // last), so a still-invalid chip's message can't be clobbered by an
  // unrelated chip resolving afterwards.
  // Di ReferenceNumberV4.vue
  private getShipmentIdDebounced(chip: ReferenceChipV4) {
    let fn = this.refNumberDebouncers.get(chip);
    if (!fn) {
      fn = debounce(() => this.runValidateShipmentId(chip), 250);
      this.refNumberDebouncers.set(chip, fn);
    }
    return fn;
  }

  private syncAggregateError(chips: ReferenceChipV4[] = this.modelValue) {
    const invalidChip = chips.find(
      (chip) => chip.status === "invalid" && chip.errorMessage
    );

    if (invalidChip) {
      this.errors.referenceNumber = invalidChip.errorMessage;
      this.$emit("update:errors", this.errors);
      return;
    }

    const hasValidExternal = chips.some(
      (chip) =>
        chip.type === ReferenceTypeV4.EXTERNAL_REF && chip.status === "valid"
    );

    if (this.isRequired && !hasValidExternal) {
      this.errors.referenceNumber = this.$t(
        "bookingV4.referenceNumber.error.externalRequired"
      ) as string;
      this.$emit("update:errors", this.errors);
      return;
    }

    this.errors.referenceNumber = "";
    this.$emit("update:errors", this.errors);
  }

  private setChipLoading(chip: ReferenceChipV4) {
    chip.status = "loading";
    chip.errorMessage = "";
    this.emitModelValue([...this.modelValue]);
  }

  private applyChipResult(
    chip: ReferenceChipV4,
    status: "valid" | "invalid",
    message = ""
  ) {
    chip.status = status;
    chip.errorMessage = message;
    const updated = [...this.modelValue];
    this.emitModelValue(updated);
    this.syncAggregateError(updated);
  }

  onKeyDown(event: KeyboardEvent) {
    if (!["Enter", ",", " "].includes(event.key)) return;
    event.preventDefault();

    const value = this.inputValue.trim();
    this.inputValue = "";
    if (!value) return;

    this.tryAddChip(value, [...this.modelValue]);
  }

  onInputBlur() {
    const value = this.inputValue.trim();
    this.inputValue = "";
    if (value) this.tryAddChip(value, [...this.modelValue]);
    if (!this.isRequired) return;
    this.syncAggregateError();
    this.emitValidationChange();
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const text = event.clipboardData?.getData("text") || "";
    const tokens = text
      .split(/[\n,;\s]+/)
      .map((token) => token.trim())
      .filter(Boolean);

    let chips = [...this.modelValue];
    this.limitError = "";
    this.duplicateTypeError = "";
    this.inputValue = "";

    tokens.forEach((token) => {
      chips = this.tryAddChip(token, chips, { silent: false });
    });
    this.$forceUpdate()
  }

  private tryAddChip(
    value: string,
    currentChips: ReferenceChipV4[],
    options: { silent?: boolean } = {}
  ): ReferenceChipV4[] {
    if (currentChips.length > this.maxItems) {
      this.limitError = this.$t("bookingV4.referenceNumber.maxLimit", {
        max: this.maxItems
      }) as string;
      return currentChips;
    }

    const type = classifyReference(value, this.accountType, this.formContext, currentChips);

    if (type === ReferenceTypeV4.STT_MANUAL && this.disabledManualStt) {
      return currentChips;
    }
    if (type === ReferenceTypeV4.EXTERNAL_REF && this.disabledRefExternal) {
      return currentChips;
    }

    const requiresSelectedClient =
      this.formContext === "client" && this.accountType !== "client";
    if (
      requiresSelectedClient &&
      type === ReferenceTypeV4.STT_MANUAL &&
      !this.selectedClient?.clientId
    ) {
      if (!options.silent) {
        this.limitError = this.$t(
          "bookingV4.referenceNumber.error.selectClientFirst"
        ) as string;
      }
      return currentChips;
    }

    // Note: an exact-duplicate value always classifies to the same type as
    // its existing chip, so the max-1-per-type check below already catches
    // duplicate values too (and reports it via duplicateTypeError) — no
    // separate value-equality check needed.
    if (currentChips.some((chip) => chip.type === type)) {
      if (!options.silent) {
        this.duplicateTypeError = this.$t(
          "bookingV4.referenceNumber.duplicateType"
        ) as string;
      }
      return currentChips;
    }

    this.limitError = "";
    if (!options.silent) this.duplicateTypeError = "";

    const chip = new ReferenceChipV4({ value, type, status: "idle" });
    const updated = [...currentChips, chip];
    this.emitModelValue(updated);
    this.validateChip(chip);

    return updated;
  }

  removeChip(index: number) {
    const chipToRemove = this.orderedChips[index];
    if (
      chipToRemove.type === ReferenceTypeV4.STT_MANUAL &&
      this.disabledManualStt
    )
      return;
    if (
      chipToRemove.type === ReferenceTypeV4.EXTERNAL_REF &&
      this.disabledRefExternal
    )
      return;
    if (
      chipToRemove.type === ReferenceTypeV4.SHIPMENT_ID &&
      this.disabledShipmentId
    )
      return;
    const updated = this.modelValue.filter((chip) => chip !== chipToRemove);
    this.refNumberDebouncers.delete(chipToRemove);
    this.sttManualDebouncers.delete(chipToRemove);
    this.emitModelValue(updated);
    this.syncAggregateError(updated);
  }

  private validateChip(chip: ReferenceChipV4) {
    if (chip.type === ReferenceTypeV4.EXTERNAL_REF) {
      this.getRefNumberDebounced(chip)();
    } else if (chip.type === ReferenceTypeV4.STT_MANUAL) {
      this.getSttManualDebounced(chip)();
    } else if (chip.type === ReferenceTypeV4.SHIPMENT_ID) {
      this.getShipmentIdDebounced(chip)();
    }
  }

  private getRefNumberDebounced(chip: ReferenceChipV4) {
    let fn = this.refNumberDebouncers.get(chip);
    if (!fn) {
      fn = debounce(() => this.runValidateRefNumber(chip), 250);
      this.refNumberDebouncers.set(chip, fn);
    }
    return fn;
  }

  private getSttManualDebounced(chip: ReferenceChipV4) {
    let fn = this.sttManualDebouncers.get(chip);
    if (!fn) {
      fn = debounce(() => this.runValidateSttManual(chip), 250);
      this.sttManualDebouncers.set(chip, fn);
    }
    return fn;
  }

  private chipSeqMap = new Map<ReferenceChipV4, number>();

  private bumpChipSeq(chip: ReferenceChipV4): number {
    const nextSeq = (this.chipSeqMap.get(chip) || 0) + 1;
    this.chipSeqMap.set(chip, nextSeq);
    return nextSeq;
  }

  setErrorMessageByCountry(res: SttReferenceExtValidation | SttManualValidation) {
    return this.countryCode.toLocaleLowerCase() === 'id' ? res.errorMessage : res.errorMessageEn
  }

  // Port of v3 validateRefNumber (stt-manual-reference/index.vue) — Zod sync check
  // then BookingController.validateSttReferenceExt async unique check.
  async runValidateRefNumber(chip: ReferenceChipV4) {
    const schema = this.isRequired
      ? sttReferenceSchema
      : sttReferenceOptionalSchema;
    const error = parseZodError(schema, chip.value);

    if (error) {
      this.applyChipResult(chip, "invalid", this.$t(error) as string);
      return;
    }

    this.setChipLoading(chip);

    await BookingController.validateSttReferenceExt({
      validateType: "no_ref_external",
      noRefExternal: chip.value
    })
      .then((res) => {
        const errorMessage = this.isForeign
          ? res.errorMessageEn
          : res.errorMessage;

        this.hasValidExternalReference = res.isAllowBooking
        this.applyChipResult(
          chip,
          res.isAllowBooking ? "valid" : "invalid",
          res.isAllowBooking ? "" : this.setErrorMessageByCountry(res)
        );
      })
      .catch((err) => {
        console.log(err);
        this.applyChipResult(chip, "invalid");
      });
  }

  // Port of v3 validateSttManual — prefix mismatch already routed to
  // external_ref at classify time, so this only runs for confirmed stt_manual chips.
  get sttManualClientId(): number {
    if (this.accountType === "client") {
      return Number(AccountController.accountData.account_type_detail.id) || 0;
    }
    return Number(this.selectedClient?.clientId) || 0;
  }

  get shipmentClientCode(): string {
    if (this.accountType === "client") {
      return AccountController.accountData.account_type_detail.code || "";
    }
    return this.selectedClient?.clientCode || "";
  }

  get countryCode() {
    return AccountController.accountData.countryCode
  }

  async runValidateSttManual(chip: ReferenceChipV4) {
    const error = parseZodError(noSttSchema, chip.value);

    if (error) {
      this.applyChipResult(chip, "invalid", error);
      return;
    }

    this.setChipLoading(chip);

    await BookingController.validateSttManual({
      clientId: this.sttManualClientId,
      sttManual: chip.value,
      bookingType: this.accountType === "pos" && this.formContext !== "client" ? "pos" : "client",
      productType: ""
    })
      .then((res) => {
        const errorMessage = this.isForeign
          ? res.errorMessageEn
          : res.errorMessage;
        this.applyChipResult(
          chip,
          res.isAllowBooking ? "valid" : "invalid",
          res.isAllowBooking ? "" : this.setErrorMessageByCountry(res)
        );
      })
      .catch(() => {
        this.applyChipResult(chip, "invalid");
      });
  }

  async runValidateShipmentId(chip: ReferenceChipV4) {
    const clientCode = this.shipmentClientCode;

    if (!clientCode) {
      this.applyChipResult(chip, "invalid");
      return;
    }

    const seq = this.bumpChipSeq(chip);
    this.setChipLoading(chip);

    try {
      const res = await BookingController.validateSttReferenceExt({
        validateType: "shipment_id",
        shipmentId: chip.value,
        clientCode: clientCode
      });

      if (this.chipSeqMap.get(chip) !== seq) return;

      const errorMessage = this.isForeign
        ? res.errorMessageEn
        : res.errorMessage;
      this.applyChipResult(
        chip,
        res.isAllowBooking ? "valid" : "invalid",
        res.isAllowBooking ? "" : this.setErrorMessageByCountry(res)
      );
    } catch (_err) {
      if (this.chipSeqMap.get(chip) !== seq) return;
      this.applyChipResult(
        chip,
        "invalid",
        this.$t("bookingV4.referenceNumber.error.systemError") as string
      );
    }
  }

  get isForeign() {
    return AccountController.accountData.accountIsForeign;
  }
}
